/* ============================================================
 * Lion Theme — global.js
 * Foundation utilities: PubSub, fetchConfig, trapFocus, onKeyUp.
 * Loaded globally (deferred). Zero dependencies, ES2020+.
 * ============================================================ */

/* ------------------------------------------------------------
 * Pub/Sub for cross-component communication
 * Events: 'cart-update', 'variant-change', 'quick-add', 'cart-error'
 * ------------------------------------------------------------ */
const PUB_SUB_EVENTS = {
  cartUpdate: "cart-update",
  quantityUpdate: "quantity-update",
  optionValueSelectionChange: "option-value-selection-change",
  variantChange: "variant-change",
  cartError: "cart-error",
};

const subscribers = {};
function subscribe(eventName, callback) {
  if (subscribers[eventName] === undefined) subscribers[eventName] = [];
  subscribers[eventName] = [...subscribers[eventName], callback];
  return function unsubscribe() {
    subscribers[eventName] = subscribers[eventName].filter(
      (cb) => cb !== callback,
    );
  };
}
function publish(eventName, data) {
  if (subscribers[eventName]) {
    subscribers[eventName].forEach((callback) => callback(data));
  }
}

window.PUB_SUB_EVENTS = PUB_SUB_EVENTS;
window.subscribe = subscribe;
window.publish = publish;

/* ------------------------------------------------------------
 * fetchConfig — standard headers for Shopify AJAX API
 * ------------------------------------------------------------ */
function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}
window.fetchConfig = fetchConfig;

/* ------------------------------------------------------------
 * Utility: debounce, trapFocus, onKeyUp
 * ------------------------------------------------------------ */
function debounce(fn, wait = 200) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, wait = 100) {
  let last = 0;
  return function (...args) {
    const now = Date.now();
    if (now - last >= wait) {
      last = now;
      fn.apply(this, args);
    }
  };
}

let trapFocusHandlers = {};
function trapFocus(container, elementToFocus = container) {
  const focusableEls = container.querySelectorAll(
    'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe',
  );
  const first = focusableEls[0];
  const last = focusableEls[focusableEls.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;
    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };
  trapFocusHandlers.focusout = () =>
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  trapFocusHandlers.keydown = (event) => {
    if (event.code !== "Tab") return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);
  elementToFocus.focus();
  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);
  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code?.toUpperCase() !== "ESCAPE") return;
  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;
  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", "false");
  summaryElement.focus();
}

window.debounce = debounce;
window.throttle = throttle;
window.trapFocus = trapFocus;
window.removeTrapFocus = removeTrapFocus;
window.onKeyUpEscape = onKeyUpEscape;

/* ------------------------------------------------------------
 * Custom element: <lion-details-disclosure> — accessible dropdown
 * ------------------------------------------------------------ */
class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector("details");
    this.content =
      this.mainDetailsToggle.querySelector("summary").nextElementSibling;
    this.mainDetailsToggle.addEventListener(
      "focusout",
      this.onFocusOut.bind(this),
    );
    this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
  }
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }
  onToggle() {
    if (!this.animations)
      this.animations = this.content.getAnimations?.() ?? [];
    if (this.mainDetailsToggle.hasAttribute("open")) {
      this.animations.forEach((anim) => anim.play());
    } else {
      this.animations.forEach((anim) => anim.cancel());
    }
  }
  close() {
    this.mainDetailsToggle.removeAttribute("open");
    this.mainDetailsToggle
      .querySelector("summary")
      .setAttribute("aria-expanded", "false");
  }
}
customElements.define("lion-details-disclosure", DetailsDisclosure);

/* ------------------------------------------------------------
 * Custom element: <lion-modal-dialog> — accessible modal with focus trap
 * ------------------------------------------------------------ */
class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]')?.addEventListener(
      "click",
      this.hide.bind(this, false),
    );
    this.addEventListener("keyup", (event) => {
      if (event.code?.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }
  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }
  show(opener) {
    this.openedBy = opener;
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    trapFocus(this, this.querySelector('[role="dialog"]'));
  }
  hide() {
    document.body.classList.remove("overflow-hidden");
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    if (this.openedBy) this.openedBy.focus();
  }
}
customElements.define("lion-modal-dialog", ModalDialog);

/* ------------------------------------------------------------
 * Scroll reveal (IntersectionObserver)
 * Applies .animate--slide-in to .scroll-trigger when in viewport.
 * Respects prefers-reduced-motion via CSS fallback.
 * ------------------------------------------------------------ */
if ("IntersectionObserver" in window) {
  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate--slide-in");
          revealObserver.unobserve(entry.target);
        }
      });
    },
    { rootMargin: "0px 0px -10% 0px" },
  );

  document.addEventListener("DOMContentLoaded", () => {
    document
      .querySelectorAll(".scroll-trigger")
      .forEach((el) => revealObserver.observe(el));
  });
}

/* ------------------------------------------------------------
 * Custom element: <lion-quantity-input> — +/- buttons synced to <input type=number>
 * Used by cart drawer, cart page, product page quantity selector.
 * ------------------------------------------------------------ */
if (!customElements.get("lion-quantity-input")) {
  class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector("input");
      if (!this.input) return;
      this.changeEvent = new Event("change", { bubbles: true });
      this.querySelectorAll("button").forEach((btn) =>
        btn.addEventListener("click", this.onButtonClick.bind(this)),
      );
      this.input.addEventListener("change", this.validateQtyRules.bind(this));
      this.validateQtyRules();
    }
    onButtonClick(event) {
      event.preventDefault();
      const prev = this.input.value;
      if (event.currentTarget.name === "plus") this.input.stepUp();
      else this.input.stepDown();
      if (prev !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }
    validateQtyRules() {
      const min = parseInt(this.input.min || 0, 10);
      const max = parseInt(this.input.max || 9999, 10);
      const val = parseInt(this.input.value, 10);
      const minusBtn = this.querySelector('[name="minus"]');
      const plusBtn = this.querySelector('[name="plus"]');
      if (minusBtn) minusBtn.disabled = val <= min;
      if (plusBtn) plusBtn.disabled = val >= max;
    }
  }
  customElements.define("lion-quantity-input", QuantityInput);
}

/* ------------------------------------------------------------
 * Viewport height fix (mobile 100vh bug)
 * ------------------------------------------------------------ */
function setViewportHeight() {
  document.documentElement.style.setProperty(
    "--vh",
    `${window.innerHeight * 0.01}px`,
  );
}
setViewportHeight();
window.addEventListener("resize", throttle(setViewportHeight, 200));

/* ------------------------------------------------------------
 * Toast notification system
 * Subscribes to 'cart-error' and 'cart-update' PubSub events.
 * ------------------------------------------------------------ */
class ToastSystem {
  constructor() {
    this.container =
      document.getElementById("toast-container") || this.createContainer();
    this.dismissTimers = new Map();
    this.listen();
  }
  createContainer() {
    const el = document.createElement("div");
    el.id = "toast-container";
    el.className = "toast-container";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    document.body.appendChild(el);
    return el;
  }
  listen() {
    if (typeof window.subscribe === "function") {
      window.subscribe(
        window.PUB_SUB_EVENTS?.cartError || "cart-error",
        (data) => {
          this.show(data?.message || "Something went wrong.", "error", 6000);
        },
      );
      window.subscribe(
        window.PUB_SUB_EVENTS?.cartUpdate || "cart-update",
        () => {
          this.show("Cart updated", "success", 2000);
        },
      );
    }
  }
  show(message, type = "info", duration = 4000) {
    const toast = document.createElement("div");
    toast.className = `toast toast--${type}`;
    toast.textContent = message;
    const closeBtn = document.createElement("button");
    closeBtn.className = "toast__close";
    closeBtn.setAttribute("aria-label", "Dismiss");
    closeBtn.textContent = "×";
    closeBtn.addEventListener("click", () => this.dismiss(toast));
    toast.appendChild(closeBtn);
    this.container.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add("is-visible"));
    const timer = setTimeout(() => this.dismiss(toast), duration);
    this.dismissTimers.set(toast, timer);
    toast.addEventListener("mouseenter", () =>
      clearTimeout(this.dismissTimers.get(toast)),
    );
    toast.addEventListener("mouseleave", () => {
      this.dismissTimers.set(
        toast,
        setTimeout(() => this.dismiss(toast), duration),
      );
    });
  }
  dismiss(toast) {
    clearTimeout(this.dismissTimers.get(toast));
    this.dismissTimers.delete(toast);
    toast.classList.remove("is-visible");
    toast.addEventListener("transitionend", () => toast.remove(), {
      once: true,
    });
    setTimeout(() => toast.remove(), 400);
  }
}

document.addEventListener("DOMContentLoaded", () => {
  window.toast = new ToastSystem();
});

